import type { HoComponents } from '@pos/api-client';
import { hoApi } from '../api';

export type TableQr = HoComponents['schemas']['TableQrDto'];
export type TableQrLink = HoComponents['schemas']['TableQrLinkDto'];

export const tableQrKey = ['ho', 'table-qr'] as const;

/** The server checks these again (ADR 0067): reading the list, and changing codes or printing the sheet (it shows the codes). */
export const TABLE_QR_READ = 'ho.online.table_qr.read';
export const TABLE_QR_MANAGE = 'ho.online.table_qr.manage';

export interface StoreOption {
  id: string;
  code: string;
  label: string;
}

export async function loadStores(): Promise<StoreOption[]> {
  const { data, error } = await hoApi.GET('/api/v1/identity/stores', {
    params: { query: { pageSize: 500, sort: 'code' } },
  });
  if (!data) throw error;
  return data.items.map((s) => ({ id: s.id, code: s.code, label: `${s.code} · ${s.name}` }));
}

/** A store's dining tables with their code, if any; never the token. */
export async function listTableQr(storeId: string): Promise<TableQr[]> {
  const { data, error } = await hoApi.GET('/api/v1/onlineordering/table-qr', { params: { query: { storeId } } });
  if (!data) throw error;
  return data;
}

/** The store's active codes with their tokens and addresses (ADR 0093), to copy a table's link. Never cached. */
export async function listTableQrLinks(storeId: string): Promise<TableQrLink[]> {
  const { data, error } = await hoApi.GET('/api/v1/onlineordering/table-qr/links', { params: { query: { storeId } } });
  if (!data) throw error;
  return data;
}

/** Codes for the store's active tables without one. The answer holds the new tokens; the page only counts them. */
export async function generateTableQr(storeId: string): Promise<TableQrLink[]> {
  const { data, error } = await hoApi.POST('/api/v1/onlineordering/table-qr/generate', { body: { storeId } });
  if (!data) throw error;
  return data;
}

/** New codes for every table of the store: every printed card stops working. */
export async function rotateStoreTableQr(storeId: string): Promise<TableQrLink[]> {
  const { data, error } = await hoApi.POST('/api/v1/onlineordering/table-qr/rotate', { body: { storeId } });
  if (!data) throw error;
  return data;
}

export async function rotateTableQr(id: string): Promise<TableQrLink> {
  const { data, error } = await hoApi.POST('/api/v1/onlineordering/table-qr/{id}/rotate', {
    params: { path: { id } },
  });
  if (!data) throw error;
  return data;
}

export async function setTableQrActive(id: string, isActive: boolean): Promise<TableQr> {
  const { data, error } = await hoApi.PUT('/api/v1/onlineordering/table-qr/{id}/active', {
    params: { path: { id } },
    body: { isActive },
  });
  if (!data) throw error;
  return data;
}

/**
 * The store's print sheet (A4 PDF, six cards a page). It shows the codes, so it is fetched with the session and saved from
 * the response in the browser, never opened from a link.
 */
export async function downloadTableQrSheet(storeId: string, fileName: string): Promise<void> {
  const { data, error, response } = (await hoApi.GET('/api/v1/onlineordering/table-qr/sheet', {
    params: { query: { storeId } },
    parseAs: 'blob',
  })) as { data?: unknown; error?: unknown; response: Response };
  if (!response.ok || !(data instanceof Blob)) throw error ?? { status: response.status };
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
